import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { LiveKitRoom, RoomAudioRenderer, useTracks, ParticipantTile, GridLayout, useLocalParticipant, useRoomContext } from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { Send, ShoppingBag, X, Radio, Users, Pin, ArrowLeft, Video, StopCircle, Gift, Coins, Heart, UserPlus, UserCheck, Share2, ShoppingCart, Plus, Star, Trash2, Minus, Wand2, Sparkles, Zap, Palette, Mic, MicOff, Camera, CameraOff, Package, Save, RotateCcw, Wifi, WifiOff, MonitorUp, Settings, Clapperboard, MoreHorizontal, Gauge, VideoOff } from "lucide-react";
import { api, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { createBeautyProcessor, createBlurProcessor, canRealBlur, isLowPerfDevice, mapBeautyOptions } from "../lib/liveFilters";
import { useLiveRealtime } from "../lib/useLiveRealtime";
import ReplayView from "../components/ReplayView";

const guestId = () => { let g = localStorage.getItem("taven_guest_id"); if (!g) { g = "g" + Math.random().toString(36).slice(2, 8); localStorage.setItem("taven_guest_id", g); } return g; };

function VideoStage() {
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], { onlySubscribed: false });
  return (
    <GridLayout tracks={tracks} style={{ height: "100%" }}>
      <ParticipantTile />
    </GridLayout>
  );
}

// Applies beauty / background-blur processors to the HOST's live camera track.
// Runs inside <LiveKitRoom> so it can read the local participant.
function HostCameraFilters({ fx, cameraMode, micOn }) {
  const { localParticipant } = useLocalParticipant();
  const beautyRef = useRef(null);
  const blurRef = useRef(null);
  const activeRef = useRef(null); // 'beauty' | 'blur' | null

  // Camera / mic enable follow the selected live mode (on | off | product_only).
  // Surface real permission / device errors so the host always knows why a control didn't work.
  const camErrRef = useRef(false);
  const micErrRef = useRef(false);
  useEffect(() => {
    if (!localParticipant) return;
    localParticipant.setCameraEnabled(cameraMode === "on").then(() => { camErrRef.current = false; }).catch((e) => {
      if (cameraMode === "on" && !camErrRef.current) {
        camErrRef.current = true;
        const name = e?.name || "";
        if (name === "NotAllowedError") toast.error("Camera permission is blocked. Allow camera access in your browser settings.");
        else if (name === "NotFoundError") toast.error("No camera found on this device.");
        else toast.error("Camera could not start.");
      }
    });
  }, [localParticipant, cameraMode]);
  useEffect(() => {
    if (!localParticipant) return;
    localParticipant.setMicrophoneEnabled(!!micOn).then(() => { micErrRef.current = false; }).catch((e) => {
      if (micOn && !micErrRef.current) {
        micErrRef.current = true;
        const name = e?.name || "";
        if (name === "NotAllowedError") toast.error("Microphone permission is blocked. Allow mic access in your browser settings.");
        else if (name === "NotFoundError") toast.error("No microphone found on this device.");
        else toast.error("Microphone could not start.");
      }
    });
  }, [localParticipant, micOn]);

  useEffect(() => {
    let cancelled = false;
    const apply = async () => {
      if (cameraMode !== "on") return; // no camera track to process
      const pub = localParticipant?.getTrackPublication(Track.Source.Camera);
      const track = pub?.track;
      if (!track) return;
      const wantBlur = fx.blur && !fx.lowPerf && canRealBlur();
      const wantBeauty = fx.beauty;
      try {
        if (wantBlur) {
          if (activeRef.current !== "blur") {
            blurRef.current = createBlurProcessor(fx.blurRadius);
            await track.setProcessor(blurRef.current);
            if (cancelled) return;
            activeRef.current = "blur";
            beautyRef.current = null;
          } else if (blurRef.current) {
            await blurRef.current.updateTransformerOptions({ blurRadius: fx.blurRadius });
          }
        } else if (wantBeauty) {
          const opts = mapBeautyOptions(fx);
          if (activeRef.current !== "beauty") {
            beautyRef.current = createBeautyProcessor(opts);
            await track.setProcessor(beautyRef.current);
            if (cancelled) return;
            activeRef.current = "beauty";
            blurRef.current = null;
          } else if (beautyRef.current) {
            await beautyRef.current.updateTransformerOptions(opts);
          }
        } else if (activeRef.current) {
          await track.stopProcessor();
          activeRef.current = null;
          beautyRef.current = null;
          blurRef.current = null;
        }
      } catch (e) {
        console.warn("[TAVEN filters] could not apply", e);
        if (fx.blur && !canRealBlur()) toast.error("Background blur isn't supported on this device.");
      }
    };
    apply();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localParticipant, cameraMode, fx.beauty, fx.blur, fx.lowPerf, fx.blurRadius, fx.brightness, fx.smooth, fx.warmth, fx.productAccurate]);

  return null;
}

// Persistent host media control bar — runs inside <LiveKitRoom> so it reflects & controls the
// real LiveKit tracks. Server-confirmed state (cameraMode/micOn come from the session), not fake.
function HostControlBar({ isLive, cameraMode, micOn, onCameraMode, onToggleMic, onOpenFilters, onOpenProducts, onInviteCohost, onEnd, onStudio }) {
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();
  const [sharing, setSharing] = useState(false);
  const [showDevices, setShowDevices] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [devices, setDevices] = useState({ videoinput: [], audioinput: [], audiooutput: [] });
  const [quality, setQuality] = useState("unknown");
  const [micLevel, setMicLevel] = useState(0);

  const camOn = cameraMode === "on";

  useEffect(() => { setSharing(!!localParticipant?.isScreenShareEnabled); }, [localParticipant?.isScreenShareEnabled]);
  useEffect(() => {
    const id = setInterval(() => setQuality(localParticipant?.connectionQuality || "unknown"), 3000);
    return () => clearInterval(id);
  }, [localParticipant]);

  // lightweight mic level meter from the local mic track (graceful if unavailable)
  useEffect(() => {
    if (!micOn || !localParticipant) { setMicLevel(0); return; }
    let raf, ctx, analyser, stop = false;
    try {
      const pub = localParticipant.getTrackPublication(Track.Source.Microphone);
      const mst = pub?.track?.mediaStreamTrack;
      if (!mst) return;
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(new MediaStream([mst]));
      analyser = ctx.createAnalyser(); analyser.fftSize = 256; src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => { if (stop) return; analyser.getByteFrequencyData(data); setMicLevel(Math.min(100, Math.round((data.reduce((a, b) => a + b, 0) / data.length) * 1.6))); raf = requestAnimationFrame(tick); };
      tick();
    } catch { /* metering unavailable */ }
    return () => { stop = true; if (raf) cancelAnimationFrame(raf); if (ctx) ctx.close().catch(() => {}); };
  }, [micOn, localParticipant]);

  const loadDevices = async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices({ videoinput: all.filter((d) => d.kind === "videoinput"), audioinput: all.filter((d) => d.kind === "audioinput"), audiooutput: all.filter((d) => d.kind === "audiooutput") });
      setShowDevices((v) => !v); setShowMore(false);
    } catch { toast.error("Devices are unavailable in this browser."); }
  };
  const switchDevice = async (kind, deviceId) => {
    try { await room.switchActiveDevice(kind, deviceId); toast.success("Device switched"); }
    catch { toast.error("That device failed — keeping the current one."); } // safe rollback (LiveKit keeps prior track)
  };
  const toggleShare = async () => {
    try { await localParticipant.setScreenShareEnabled(!sharing); setSharing(!sharing); }
    catch { toast.error("Screen sharing isn't available in this browser."); }
  };

  const qColor = quality === "poor" || quality === "lost" ? "text-red-400" : quality === "unknown" ? "text-white/40" : "text-green-400";
  const Btn = ({ onClick, icon: I, label, active, danger, testid, primary }) => (
    <button onClick={onClick} data-testid={testid} title={label}
      className={`relative flex flex-col items-center justify-center gap-0.5 min-w-[52px] px-2 py-1.5 rounded-md transition-colors ${danger ? "text-red-300 hover:bg-red-600/30" : active ? "text-taven-gold bg-taven-gold/15" : primary ? "text-taven-gold hover:bg-white/10" : "text-white/80 hover:bg-white/10"}`}>
      <I className="w-5 h-5" /><span className="text-[9px] font-bold uppercase tracking-wide">{label}</span>
    </button>
  );

  return (
    <div className="absolute bottom-0 inset-x-0 z-30" data-testid="host-control-bar" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="bg-gradient-to-t from-black via-black/90 to-transparent pt-8 pb-2 px-2">
        {/* status strip */}
        <div className="flex items-center justify-center gap-3 mb-1.5">
          <span className={`flex items-center gap-1 text-[10px] font-bold uppercase ${qColor}`} data-testid="host-connection-quality"><Gauge className="w-3 h-3" /> {quality === "unknown" ? "Connecting" : quality}</span>
          {micOn && (
            <span className="flex items-center gap-1" data-testid="host-mic-level" title="Mic level">
              <Mic className="w-3 h-3 text-taven-gold" />
              <span className="w-16 h-1.5 bg-white/15 rounded-full overflow-hidden"><span className="block h-full bg-taven-gold transition-[width] duration-100" style={{ width: `${micLevel}%` }} /></span>
            </span>
          )}
        </div>
        {/* control row */}
        <div className="flex items-center justify-center gap-1 flex-nowrap overflow-x-auto">
          <Btn onClick={onToggleMic} icon={micOn ? Mic : MicOff} label={micOn ? "Mic" : "Muted"} active={micOn} testid="hostbar-mic" />
          <Btn onClick={() => onCameraMode(camOn ? "off" : "on")} icon={camOn ? Camera : CameraOff} label={camOn ? "Camera" : "Cam Off"} active={camOn} testid="hostbar-camera" />
          <Btn onClick={toggleShare} icon={MonitorUp} label="Share" active={sharing} testid="hostbar-share" />
          <div className="relative">
            <Btn onClick={loadDevices} icon={Settings} label="Devices" active={showDevices} testid="hostbar-devices" />
            {showDevices && (
              <div className="absolute bottom-16 left-1/2 -translate-x-1/2 w-64 bg-[#151515] border border-white/15 p-3 space-y-2 z-40 rounded-md" data-testid="hostbar-device-menu">
                {[["videoinput", "Camera"], ["audioinput", "Microphone"], ["audiooutput", "Speaker"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-[10px] font-bold uppercase text-white/50">{l}</label>
                    <select onChange={(e) => switchDevice(k, e.target.value)} data-testid={`hostbar-device-${k}`} className="w-full bg-white/5 border border-white/15 px-2 py-1.5 text-xs mt-0.5 rounded">
                      <option value="">Default</option>
                      {devices[k].map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || `${l} ${d.deviceId.slice(0, 4)}`}</option>)}
                    </select>
                  </div>
                ))}
                {devices.videoinput.length === 0 && devices.audioinput.length === 0 && <p className="text-[10px] text-white/40">No media devices detected.</p>}
              </div>
            )}
          </div>
          {/* direct on desktop, in More on small screens */}
          <div className="hidden sm:flex items-center gap-1">
            <Btn onClick={onOpenFilters} icon={Wand2} label="Look" testid="hostbar-filters" />
            <Btn onClick={onOpenProducts} icon={ShoppingBag} label="Products" testid="hostbar-products" />
            <Btn onClick={onInviteCohost} icon={UserPlus} label="Co-host" testid="hostbar-cohost" />
            <Btn onClick={onStudio} icon={Clapperboard} label="Studio" primary testid="hostbar-studio" />
          </div>
          <div className="relative sm:hidden">
            <Btn onClick={() => { setShowMore((v) => !v); setShowDevices(false); }} icon={MoreHorizontal} label="More" active={showMore} testid="hostbar-more" />
            {showMore && (
              <div className="absolute bottom-16 right-0 w-44 bg-[#151515] border border-white/15 p-1.5 z-40 rounded-md flex flex-col" data-testid="hostbar-more-menu">
                <button onClick={() => { onOpenFilters(); setShowMore(false); }} className="flex items-center gap-2 px-3 py-2 text-xs text-white/80 hover:bg-white/10 rounded" data-testid="hostbar-filters-m"><Wand2 className="w-4 h-4" /> Look & Filters</button>
                <button onClick={() => { onOpenProducts(); setShowMore(false); }} className="flex items-center gap-2 px-3 py-2 text-xs text-white/80 hover:bg-white/10 rounded" data-testid="hostbar-products-m"><ShoppingBag className="w-4 h-4" /> Products</button>
                <button onClick={() => { onInviteCohost(); setShowMore(false); }} className="flex items-center gap-2 px-3 py-2 text-xs text-white/80 hover:bg-white/10 rounded" data-testid="hostbar-cohost-m"><UserPlus className="w-4 h-4" /> Invite Co-host</button>
                <button onClick={() => { onStudio(); setShowMore(false); }} className="flex items-center gap-2 px-3 py-2 text-xs text-taven-gold hover:bg-white/10 rounded" data-testid="hostbar-studio-m"><Clapperboard className="w-4 h-4" /> Studio Tools</button>
              </div>
            )}
          </div>
          {isLive && <Btn onClick={onEnd} icon={StopCircle} label="End" danger testid="hostbar-end" />}
        </div>
      </div>
    </div>
  );
}

export default function LiveRoom() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const { user, authApi } = useAuth();
  const { addItem, updateQty, removeItem, items, count, subtotal, checkout } = useCart();
  const [session, setSession] = useState(null);
  const [conn, setConn] = useState(null);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [products, setProducts] = useState([]);
  const lastTs = useRef("");
  const chatEnd = useRef(null);
  const [gifts, setGifts] = useState([]);          // gift catalog
  const [balance, setBalance] = useState(null);
  const [showGifts, setShowGifts] = useState(false);
  const [flying, setFlying] = useState([]);        // animated gift feed
  const giftTs = useRef("");
  const [likes, setLikes] = useState(0);
  const [hearts, setHearts] = useState([]);        // floating heart animation
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [showCart, setShowCart] = useState(false);
  const [showShop, setShowShop] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [cameraMode, setCameraMode] = useState("on"); // on | off | product_only
  const [micOn, setMicOn] = useState(true);
  const [rtViewers, setRtViewers] = useState(null); // live viewer presence from WebSocket
  const [fx, setFx] = useState({
    beauty: false, brightness: 40, smooth: 45, warmth: 35,
    blur: false, blurRadius: 8,
    productAccurate: false, lowPerf: isLowPerfDevice(),
  });

  const isHost = user && session && session.host_email === user.email;

  // ---- Real-time transport (chat, reactions, presence, pin/product, session-state) ----
  const rtName = user ? (user.first_name || user.email) : "Guest";
  const onRealtime = useCallback((type, data) => {
    if (type === "chat") {
      setChat((c) => (c.some((m) => m.id === data.id) ? c : [...c, { id: data.id, name: data.name, message: data.message }]));
    } else if (type === "reaction") {
      spawnHeart();
    } else if (type === "like") {
      if (data?.likes != null) setLikes((l) => Math.max(l, data.likes));
    } else if (type === "viewer_count") {
      if (data?.viewers != null) setRtViewers(data.viewers);
    } else if (type === "pin") {
      setSession((s) => (s ? { ...s, pinned_product: data.pinned_product ?? null, ...(data.products ? { products: data.products } : {}) } : s));
    } else if (type === "products") {
      setSession((s) => (s ? { ...s, products: data.products || [] } : s));
    } else if (type === "session_state") {
      if (data?.status) setSession((s) => (s ? { ...s, status: data.status } : s));
      if (data?.status === "ended") toast("This live has ended.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const rtSessionId = session && session.status !== "ended" ? sessionId : null;
  const { status: rtStatus, send: rtSend, connected: rtConnected } = useLiveRealtime(rtSessionId, rtName, onRealtime);

  // Load the host's saved "default look" preset and auto-apply it.
  useEffect(() => {
    if (!user) return;
    authApi.get("/live/host/preset").then(({ data }) => {
      if (data.preset) setFx((f) => ({ ...f, ...data.preset, lowPerf: data.preset.lowPerf != null ? data.preset.lowPerf : f.lowPerf }));
    }).catch(() => {});
  }, [user]);

  // Viewers follow the host's chosen camera mode from the polled session.
  useEffect(() => {
    if (isHost) return;
    if (session?.camera_mode) setCameraMode(session.camera_mode);
    if (session?.mic_on != null) setMicOn(session.mic_on);
  }, [session?.camera_mode, session?.mic_on, isHost]);

  const saveLook = async () => {
    const { beauty, brightness, smooth, warmth, blur, blurRadius, productAccurate, lowPerf } = fx;
    try {
      await authApi.put("/live/host/preset", { beauty, brightness, smooth, warmth, blur, blurRadius, productAccurate, lowPerf });
      toast.success("Saved as your default look — it'll auto-apply every live.");
    } catch { toast.error("Could not save your default look."); }
  };
  const resetLook = async () => {
    try {
      await authApi.delete("/live/host/preset");
      setFx((f) => ({ ...f, beauty: false, brightness: 40, smooth: 45, warmth: 35, blur: false, blurRadius: 8, productAccurate: false }));
      toast.success("Default look reset.");
    } catch { toast.error("Could not reset."); }
  };
  const changeCameraMode = async (mode) => {
    setCameraMode(mode);
    try { await authApi.put(`/live/sessions/${sessionId}/camera-mode`, { camera_mode: mode, mic_on: micOn }); } catch {}
  };
  const toggleMic = async () => {
    const v = !micOn; setMicOn(v);
    try { await authApi.put(`/live/sessions/${sessionId}/camera-mode`, { camera_mode: cameraMode, mic_on: v }); } catch {}
  };


  useEffect(() => {
    let active = true;
    const load = async () => { try { const { data } = await api.get(`/live/sessions/${sessionId}`); if (active) setSession(data); } catch {} };
    load(); const t = setInterval(load, 5000); return () => { active = false; clearInterval(t); };
  }, [sessionId]);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/live/sessions/${sessionId}/chat`, { params: lastTs.current ? { after: lastTs.current } : {} });
        if (active && data.messages.length) {
          setChat((c) => {
            const seen = new Set(c.map((m) => m.id));
            const fresh = data.messages.filter((m) => !seen.has(m.id));
            return fresh.length ? [...c, ...fresh] : c;
          });
          lastTs.current = data.messages[data.messages.length - 1].created_at;
        }
      } catch {}
    };
    poll(); const t = setInterval(poll, 3000); return () => { active = false; clearInterval(t); };
  }, [sessionId]);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [chat]);

  // gift catalog + wallet balance
  useEffect(() => { api.get("/gifts/catalog").then(({ data }) => setGifts(data.gifts || [])).catch(() => {}); }, []);
  useEffect(() => { if (user) authApi.get("/coins/wallet").then(({ data }) => setBalance(data.balance)).catch(() => {}); else setBalance(null); }, [user]);

  // gift feed poll → animate
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/live/sessions/${sessionId}/gifts`, { params: giftTs.current ? { after: giftTs.current } : {} });
        if (active && data.gifts.length) {
          giftTs.current = data.gifts[data.gifts.length - 1].created_at;
          data.gifts.forEach((g) => {
            const id = g.id + Math.random();
            setFlying((f) => [...f, { ...g, _id: id }]);
            setTimeout(() => setFlying((f) => f.filter((x) => x._id !== id)), 4000);
          });
        }
      } catch {}
    };
    poll(); const t = setInterval(poll, 3000); return () => { active = false; clearInterval(t); };
  }, [sessionId]);

  // sync like count from server session (keep the higher value to avoid flicker)
  useEffect(() => { if (session?.likes != null) setLikes((l) => Math.max(l, session.likes)); }, [session?.likes]);

  // follow status for this host
  useEffect(() => {
    if (!session?.host_email || isHost) return;
    api.get(`/live/host/${encodeURIComponent(session.host_email)}/follow-status`, {
      headers: user ? { Authorization: `Bearer ${localStorage.getItem("taven_user_token")}` } : {},
    }).then(({ data }) => { setFollowing(data.following); setFollowers(data.followers); }).catch(() => {});
  }, [session?.host_email, isHost, user]);

  // Qualified live-view ping (30s) for the monetization engine.
  useEffect(() => {
    if (!session || isHost) return;
    const t = setTimeout(() => {
      let dev = localStorage.getItem("taven_dev_id");
      if (!dev) { dev = Math.random().toString(36).slice(2); localStorage.setItem("taven_dev_id", dev); }
      api.post(`/live/sessions/${sessionId}/view`, { watch_seconds: 30, device_id: dev }).catch(() => {});
    }, 30000);
    return () => clearTimeout(t);
  }, [session?.id, isHost]);

  const spawnHeart = () => {
    const id = Math.random().toString(36).slice(2);
    const left = 10 + Math.random() * 60;      // horizontal jitter
    const drift = (Math.random() - 0.5) * 40;  // sideways drift
    const hue = ["#ff2d55", "#ff6b81", "#e0b64e", "#ff8a3d"][Math.floor(Math.random() * 4)];
    setHearts((h) => [...h.slice(-14), { id, left, drift, hue }]);
    setTimeout(() => setHearts((h) => h.filter((x) => x.id !== id)), 2200);
  };

  const like = async () => {
    setLikes((l) => l + 1);
    // When realtime is connected, the reaction echo spawns the heart for everyone (incl. self).
    if (rtConnected) rtSend({ type: "reaction", kind: "like" }); else spawnHeart();
    try { const { data } = await api.post(`/live/sessions/${sessionId}/like`); setLikes((l) => Math.max(l, data.likes)); } catch {}
  };

  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow this host."); nav("/login", { state: { from: `/live/${sessionId}` } }); return; }
    try {
      const { data } = await authApi.post(`/live/host/${encodeURIComponent(session.host_email)}/follow`);
      setFollowing(data.following); setFollowers(data.followers);
      toast.success(data.following ? `Following ${session.host_name || "host"}` : "Unfollowed");
    } catch { toast.error("Could not update follow."); }
  };

  const invite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    try { await authApi.post(`/live/sessions/${sessionId}/invite`, { email }); toast.success(`Invited ${email} as co-host`); setInviteEmail(""); setShowInvite(false); }
    catch { toast.error("Could not send co-host invite."); }
  };

  const share = async () => {
    const url = window.location.href;
    try { if (navigator.share) { await navigator.share({ title: session.title, url }); } else { await navigator.clipboard.writeText(url); toast.success("Live link copied"); } } catch {}
  };

  const sendGift = async (key) => {
    if (!user) { toast.error("Sign in to send gifts."); return; }
    try {
      const { data } = await authApi.post(`/live/sessions/${sessionId}/gift`, { gift_key: key });
      setBalance(data.balance); setShowGifts(false);
    } catch (e) {
      if (e?.response?.status === 400) { toast.error("Not enough coins — top up your wallet."); nav("/coins"); }
      else toast.error("Could not send gift.");
    }
  };

  const joinViewer = async () => { try { const { data } = await api.post("/live/token", { session_id: sessionId, identity: guestId(), name: user ? user.first_name : "Guest" }); setConn({ token: data.token, url: data.url }); } catch { /* not live yet */ } };
  useEffect(() => { if (session && !conn && !isHost && session.status === "live") joinViewer(); }, [session, isHost]);

  const goLive = async () => { try { const { data } = await authApi.post(`/live/sessions/${sessionId}/start`); setConn({ token: data.token, url: data.url, host: true }); setSession((s) => ({ ...s, status: "live" })); toast.success("You are live!"); } catch { toast.error("Only the host can go live."); } };
  const endLive = async () => { try { await authApi.post(`/live/sessions/${sessionId}/end`); toast.success("Live ended."); nav("/live"); } catch {} };

  const send = async () => {
    const m = msg.trim(); if (!m) return; setMsg("");
    // Prefer the real-time socket (server persists + echoes); fall back to REST.
    if (rtConnected && rtSend({ type: "chat", message: m })) return;
    try { await api.post(`/live/sessions/${sessionId}/chat`, { name: user ? user.first_name : "Guest", message: m }); } catch {}
  };

  const openPicker = async () => { setShowPicker(true); if (!products.length) { try { const { data } = await api.get("/shop/products"); setProducts((Array.isArray(data) ? data : data.products || data.items || []).slice(0, 40)); } catch {} } };
  const track = (event, handle) => { api.post(`/live/sessions/${sessionId}/track`, { event, handle }).catch(() => {}); };
  const refreshSession = async () => { try { const { data } = await api.get(`/live/sessions/${sessionId}`); setSession(data); } catch {} };
  // Host: add a TAVEN product to the live showcase (backend enforces TAVEN-only + block list).
  const addToLive = async (handle) => { try { await authApi.put(`/live/sessions/${sessionId}/products/add`, { handle }); toast.success("Added to live showcase"); refreshSession(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not add product"); } };
  const feature = async (handle) => { try { await authApi.put(`/live/sessions/${sessionId}/pin`, { handle }); toast.success("Featured on stage"); refreshSession(); } catch { toast.error("Could not feature product"); } };
  const removeFromLive = async (handle) => { try { await authApi.put(`/live/sessions/${sessionId}/products/remove`, { handle }); refreshSession(); } catch {} };
  const unpin = async () => { try { await authApi.put(`/live/sessions/${sessionId}/pin`, { handle: null }); refreshSession(); } catch {} };

  const pinned = session?.pinned_product;
  const showcase = session?.products || [];
  // Re-pull LIVE Shopify stock/price for a product before the shopper commits.
  const revalidate = async (handle) => {
    try {
      const { data } = await api.get(`/live/sessions/${sessionId}/product/${encodeURIComponent(handle)}/revalidate`);
      setSession((s) => s ? {
        ...s,
        products: (s.products || []).map((p) => p.handle === handle ? { ...p, ...data } : p),
        pinned_product: s.pinned_product?.handle === handle ? { ...s.pinned_product, ...data } : s.pinned_product,
      } : s);
      return data;
    } catch { return null; }
  };
  // Add ANY TAVEN product to the shared cart (secure TAVEN checkout on shop.taven.store).
  const addProductToCart = async (p) => {
    if (!p?.variantId) { toast.error("Product unavailable"); return false; }
    const fresh = await revalidate(p.handle);
    const avail = fresh ? (fresh.available && !fresh.blocked) : p.available;
    if (!avail) { toast.error("Just sold out — this item is no longer available."); return false; }
    const use = fresh ? { ...p, price: fresh.price ?? p.price, variantId: fresh.variantId || p.variantId } : p;
    const ok = addItem({ defaultVariantId: use.variantId, handle: use.handle, title: use.title, image: use.image, price: use.price, currency: use.currency });
    if (ok) { toast.success("Added to live cart"); track("add_to_cart", use.handle); }
    return ok;
  };
  const buyProductNow = async (p) => {
    if (!p?.variantId) { toast.error("Product unavailable"); return; }
    const fresh = await revalidate(p.handle);
    const avail = fresh ? (fresh.available && !fresh.blocked) : p.available;
    if (!avail) { toast.error("Just sold out — this item is no longer available."); return; }
    const use = fresh ? { ...p, price: fresh.price ?? p.price, variantId: fresh.variantId || p.variantId } : p;
    track("checkout", use.handle);
    const url = await checkout({ creator_email: session.host_email, seller_email: session.host_email, session_id: sessionId, product_handle: use.handle, price: use.price, viewer_email: user?.email }, [{ variantId: use.variantId, quantity: 1 }]);
    if (url) window.location.href = url;
  };
  const addToCart = () => addProductToCart(pinned);
  const buyNow = () => buyProductNow(pinned);
  const cartCheckout = async () => { track("checkout", pinned?.handle); const attr = pinned ? { creator_email: session.host_email, seller_email: session.host_email, session_id: sessionId, product_handle: pinned.handle, price: pinned.price, viewer_email: user?.email } : null; const url = await checkout(attr); if (url) window.location.href = url; };

  if (!session) return <div className="py-24 text-center text-muted-foreground">Loading live session…</div>;
  // Ended sessions render the recorded/shoppable replay experience.
  if (session.status === "ended") return <ReplayView sessionId={sessionId} />;

  return (
    <div className="bg-taven-black min-h-screen text-white" data-testid="live-room">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <Link to="/live" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back</Link>
        <div className="flex items-center gap-2 text-sm">
          {session.status === "live" && <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-red-600 px-2 py-1"><Radio className="w-3 h-3" /> Live</span>}
          <span className="font-bold line-clamp-1">{session.title}</span>
        </div>
        <div className="flex items-center gap-2">
        {session.status === "live" && (
          <span data-testid="rt-status" title={rtConnected ? "Real-time connected" : "Reconnecting — updates may be delayed"}
            className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${rtConnected ? "text-green-400 bg-green-400/10" : "text-white/50 bg-white/10"}`}>
            {rtConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}{rtConnected ? "Live" : "Sync"}
          </span>
        )}
        <span className="flex items-center gap-1 text-xs text-white/60" data-testid="live-viewer-count"><Users className="w-3.5 h-3.5" /> {rtViewers != null ? rtViewers : (session.viewer_count || 0)}</span>
        <button onClick={() => setShowCart(true)} data-testid="live-cart-btn" className="relative flex items-center gap-1 text-xs font-bold text-white bg-white/10 hover:bg-white/20 px-2.5 py-1.5 rounded-full">
          <ShoppingCart className="w-3.5 h-3.5" /> Cart
          {count > 0 && <span className="absolute -top-1.5 -right-1.5 bg-taven-orange text-white text-[10px] font-black min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1" data-testid="live-cart-count">{count}</span>}
        </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-0 max-w-7xl mx-auto">
        {/* Stage */}
        <div className="relative bg-black aspect-[9/16] sm:aspect-video lg:aspect-auto lg:h-[70vh]" data-testid="live-stage">
          {conn ? (
            <LiveKitRoom serverUrl={conn.url} token={conn.token} connect video={!!conn.host} audio={!!conn.host} style={{ height: "100%" }}>
              <VideoStage />
              {conn.host && <HostCameraFilters fx={fx} cameraMode={cameraMode} micOn={micOn} />}
              {conn.host && (
                <HostControlBar
                  isLive={session.status === "live"}
                  cameraMode={cameraMode}
                  micOn={micOn}
                  onCameraMode={changeCameraMode}
                  onToggleMic={toggleMic}
                  onOpenFilters={() => setShowFilters(true)}
                  onOpenProducts={openPicker}
                  onInviteCohost={() => setShowInvite(true)}
                  onEnd={endLive}
                  onStudio={() => nav(`/live/stage/${sessionId}`)}
                />
              )}
              <RoomAudioRenderer />
            </LiveKitRoom>
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-4">
              <Video className="w-14 h-14 text-taven-gold/50" />
              {isHost && session.status !== "ended" ? (
                <button onClick={goLive} data-testid="host-go-live" className="bg-taven-orange text-white px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#d9561a]">Go Live Now</button>
              ) : session.status === "ended" ? (
                <p className="text-white/60 text-sm">This session has ended.</p>
              ) : (
                <p className="text-white/60 text-sm">Waiting for the host to go live…</p>
              )}
            </div>
          )}

          {/* Pinned product overlay */}
          {pinned && (
            <div className={`absolute ${isHost && conn ? "bottom-28" : "bottom-3"} left-3 right-3 sm:right-auto sm:w-80 bg-white text-black p-3 flex items-center gap-3 shadow-xl`} data-testid="pinned-product">
              {pinned.image && <img src={pinned.image} alt="" className="w-14 h-14 object-cover" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold line-clamp-1">{pinned.title}</p>
                <p className="text-sm text-taven-orange font-black">{money(pinned.price)}</p>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={buyNow} data-testid="live-buy-now" className="bg-taven-orange text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-[#d9561a]">Buy Now</button>
                <button onClick={addToCart} data-testid="live-add-cart" className="bg-taven-black text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-taven-gold hover:text-black flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Add</button>
              </div>
            </div>
          )}

          {/* Camera Off / Product-Only branded placeholder */}
          {conn && cameraMode !== "on" && (
            <div className="absolute inset-0 z-10 bg-gradient-to-b from-taven-black to-[#1a1206] flex flex-col items-center justify-center gap-4 p-6" data-testid="camera-off-placeholder">
              <div className="w-24 h-24 rounded-full bg-taven-gold/15 border-2 border-taven-gold flex items-center justify-center">
                <span className="text-3xl font-black font-display text-taven-gold">{(session.host_name || "T").slice(0, 1).toUpperCase()}</span>
              </div>
              <div className="text-center">
                <p className="font-display font-black text-lg">{session.host_name || "TAVEN Host"}</p>
                <p className="text-xs uppercase tracking-widest text-taven-gold mt-1" data-testid="camera-mode-label">{cameraMode === "product_only" ? "Product Showcase" : "Audio Live"}</p>
              </div>
              {cameraMode === "product_only" && pinned && (
                <div className="bg-white text-black w-full max-w-xs p-4 mt-2" data-testid="product-only-spotlight">
                  {pinned.image && <img src={pinned.image} alt="" className="w-full aspect-square object-contain bg-white" />}
                  <p className="font-bold mt-2 line-clamp-2">{pinned.title}</p>
                  <p className="text-taven-orange font-black text-lg">{money(pinned.price)}</p>
                  <div className="flex gap-2 mt-2">
                    <button onClick={buyNow} data-testid="spotlight-buy-now" className="flex-1 bg-taven-orange text-white text-xs font-bold uppercase tracking-wider py-2 hover:bg-[#d9561a]">Buy Now</button>
                    <button onClick={addToCart} data-testid="spotlight-add-cart" className="flex-1 bg-taven-black text-white text-xs font-bold uppercase tracking-wider py-2 hover:bg-taven-gold hover:text-black">Add</button>
                  </div>
                </div>
              )}
              {cameraMode === "product_only" && !pinned && isHost && (
                <p className="text-white/50 text-xs text-center max-w-xs">Feature a product from the <strong>Products</strong> panel to spotlight it here.</p>
              )}
              <span className="text-[10px] text-white/40 uppercase tracking-widest mt-2">TAVEN Live</span>
            </div>
          )}

          {/* Host quick controls (top-right). Full media control bar is docked at the bottom. */}
          {isHost && (
            <div className="absolute top-3 right-3 flex gap-2" data-testid="host-controls">
              <button onClick={openPicker} className="bg-taven-gold text-black text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 flex items-center gap-1" data-testid="pin-product-btn"><ShoppingBag className="w-3 h-3" /> Products</button>
              {pinned && <button onClick={unpin} className="bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5">Unpin</button>}
            </div>
          )}
          {/* Animated gift feed */}
          <div className="absolute bottom-24 left-3 flex flex-col-reverse gap-2 pointer-events-none" data-testid="gift-feed">
            {flying.map((g) => (
              <div key={g._id} className="flex items-center gap-2 bg-black/60 text-white px-3 py-1.5 rounded-full text-sm animate-in slide-in-from-bottom-4 fade-in duration-500">
                <span className="text-xl">{g.emoji}</span><span className="font-bold text-taven-gold">{g.from_name}</span> sent {g.gift_name}
              </div>
            ))}
          </div>

          {/* Floating hearts layer */}
          <div className="absolute bottom-28 right-4 w-16 h-64 pointer-events-none overflow-hidden" data-testid="hearts-layer">
            {hearts.map((h) => (
              <Heart key={h.id} fill={h.hue} stroke={h.hue}
                className="absolute bottom-0 taven-heart-float"
                style={{ left: `${h.left}%`, "--drift": `${h.drift}px`, width: 26, height: 26 }} />
            ))}
          </div>

          {/* TikTok-style action rail */}
          <div className="absolute right-3 bottom-28 flex flex-col items-center gap-4 z-20" data-testid="live-action-rail">
            {!isHost && (
              <button onClick={toggleFollow} data-testid="follow-host-btn"
                className={`flex flex-col items-center gap-1 ${following ? "text-taven-gold" : "text-white"}`}>
                <span className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${following ? "bg-taven-gold text-black" : "bg-white/15 hover:bg-white/25"}`}>
                  {following ? <UserCheck className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                </span>
                <span className="text-[10px] font-bold">{followers}</span>
              </button>
            )}
            <button onClick={like} data-testid="like-btn" className="flex flex-col items-center gap-1 text-white active:scale-90 transition-transform">
              <span className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center">
                <Heart className="w-5 h-5" fill="#ff2d55" stroke="#ff2d55" />
              </span>
              <span className="text-[10px] font-bold" data-testid="like-count">{likes}</span>
            </button>
            <button onClick={share} data-testid="share-btn" className="flex flex-col items-center gap-1 text-white">
              <span className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center">
                <Share2 className="w-5 h-5" />
              </span>
              <span className="text-[10px] font-bold">Share</span>
            </button>
            {showcase.length > 0 && (
              <button onClick={() => setShowShop(true)} data-testid="live-shop-btn" className="flex flex-col items-center gap-1 text-white">
                <span className="w-11 h-11 rounded-full bg-taven-orange hover:bg-[#d9561a] flex items-center justify-center relative">
                  <ShoppingBag className="w-5 h-5" />
                  <span className="absolute -top-1 -right-1 bg-taven-gold text-black text-[10px] font-black min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">{showcase.length}</span>
                </span>
                <span className="text-[10px] font-bold">Shop</span>
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col h-[50vh] lg:h-[70vh] border-l border-white/10" data-testid="live-chat">
          <div className="px-4 py-3 border-b border-white/10 text-xs font-bold uppercase tracking-wider text-taven-gold">Live Chat</div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {chat.map((c) => <div key={c.id} className="text-sm"><span className="font-bold text-taven-gold">{c.name}: </span><span className="text-white/85">{c.message}</span></div>)}
            {chat.length === 0 && <p className="text-white/40 text-sm">Say hello to the host 👋</p>}
            <div ref={chatEnd} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/10 flex gap-2 items-center relative">
            <button type="button" onClick={() => setShowGifts((v) => !v)} data-testid="gift-button" className="bg-taven-gold text-black p-2.5 hover:bg-white shrink-0"><Gift className="w-4 h-4" /></button>
            <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a message…" data-testid="live-chat-input" className="flex-1 bg-white/10 px-3 py-2.5 text-sm outline-none placeholder:text-white/40" />
            <button data-testid="live-chat-send" className="bg-taven-orange p-2.5 hover:bg-[#d9561a]"><Send className="w-4 h-4" /></button>

            {showGifts && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-taven-black border border-white/10 p-3" data-testid="gift-tray">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-taven-gold">Send a Gift</span>
                  <Link to="/coins" className="flex items-center gap-1 text-xs text-white/70 hover:text-taven-gold"><Coins className="w-3.5 h-3.5" /> {balance ?? 0}</Link>
                </div>
                <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                  {gifts.map((g) => (
                    <button key={g.key} type="button" onClick={() => sendGift(g.key)} data-testid={`gift-${g.key}`} className="flex flex-col items-center gap-0.5 p-2 hover:bg-white/10 border border-white/5">
                      <span className="text-2xl">{g.emoji}</span>
                      <span className="text-[9px] text-white/60 flex items-center gap-0.5"><Coins className="w-2.5 h-2.5 text-taven-gold" />{g.coins}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Beauty / Camera filters (host) */}
      {showFilters && (
        <div className="fixed inset-0 z-[85] bg-black/70 flex items-end sm:items-center justify-center" onClick={() => setShowFilters(false)}>
          <div className="bg-taven-black text-white border border-white/10 w-full sm:max-w-md max-h-[88vh] overflow-y-auto p-5 rounded-t-2xl sm:rounded-none" onClick={(e) => e.stopPropagation()} data-testid="filters-panel">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-black font-display flex items-center gap-2"><Wand2 className="w-5 h-5 text-taven-gold" /> Camera Filters</h3>
              <button onClick={() => setShowFilters(false)} data-testid="close-filters-btn"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-white/50 mb-4">Enhance your lighting and background without distorting your products.</p>

            {/* Camera / Live mode */}
            <div className="border-t border-white/10 pt-3">
              <p className="text-xs font-bold uppercase tracking-wider text-taven-gold mb-2">Live Mode</p>
              <div className="grid grid-cols-3 gap-2" data-testid="camera-mode-switch">
                {[
                  { k: "on", label: "Camera On", icon: Camera },
                  { k: "off", label: "Camera Off", icon: CameraOff },
                  { k: "product_only", label: "Product Only", icon: Package },
                ].map((m) => (
                  <button key={m.k} onClick={() => changeCameraMode(m.k)} data-testid={`camera-mode-${m.k}`}
                    className={`flex flex-col items-center gap-1 py-2.5 border text-[10px] font-bold uppercase tracking-wide ${cameraMode === m.k ? "border-taven-gold bg-taven-gold/15 text-taven-gold" : "border-white/15 text-white/70 hover:border-white/40"}`}>
                    <m.icon className="w-4 h-4" />{m.label}
                  </button>
                ))}
              </div>
              <button onClick={toggleMic} data-testid="toggle-mic" className={`mt-2 w-full flex items-center justify-center gap-2 py-2 border text-xs font-bold uppercase tracking-wider ${micOn ? "border-taven-gold text-taven-gold" : "border-white/15 text-white/60"}`}>
                {micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />} {micOn ? "Mic On" : "Mic Off"}
              </button>
              {cameraMode !== "on" && <p className="text-[11px] text-white/50 mt-2">Camera is off — beauty & blur apply only when your camera is on. Your audio {micOn ? "stays live" : "is muted"}.</p>}
            </div>

            {/* Beauty toggle */}
            <div className="flex items-center justify-between py-3 border-t border-white/10">
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-taven-gold" /><span className="text-sm font-bold">Beauty</span></div>
              <button onClick={() => setFx((f) => ({ ...f, beauty: !f.beauty }))} data-testid="toggle-beauty"
                className={`relative w-11 h-6 rounded-full transition-colors ${fx.beauty ? "bg-taven-gold" : "bg-white/20"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${fx.beauty ? "translate-x-5" : ""}`} />
              </button>
            </div>
            {fx.beauty && (
              <div className="space-y-3 pb-3">
                {[
                  { key: "brightness", label: "Brightness", icon: Zap },
                  { key: "smooth", label: "Smooth Skin", icon: Sparkles, disabled: fx.productAccurate },
                  { key: "warmth", label: "Warmth", icon: Palette, disabled: fx.productAccurate },
                ].map((s) => (
                  <div key={s.key} className={s.disabled ? "opacity-40" : ""}>
                    <div className="flex items-center justify-between text-xs mb-1"><span className="flex items-center gap-1.5"><s.icon className="w-3.5 h-3.5 text-taven-gold" />{s.label}</span><span className="text-white/50">{fx[s.key]}%</span></div>
                    <input type="range" min="0" max="100" value={fx[s.key]} disabled={s.disabled} data-testid={`slider-${s.key}`}
                      onChange={(e) => setFx((f) => ({ ...f, [s.key]: Number(e.target.value) }))}
                      className="w-full accent-taven-gold" />
                  </div>
                ))}
              </div>
            )}

            {/* Background blur toggle */}
            <div className="flex items-center justify-between py-3 border-t border-white/10">
              <div className="flex items-center gap-2"><Video className="w-4 h-4 text-taven-gold" /><span className="text-sm font-bold">Background Blur</span></div>
              <button onClick={() => setFx((f) => ({ ...f, blur: !f.blur }))} data-testid="toggle-blur"
                className={`relative w-11 h-6 rounded-full transition-colors ${fx.blur ? "bg-taven-gold" : "bg-white/20"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${fx.blur ? "translate-x-5" : ""}`} />
              </button>
            </div>
            {fx.blur && (
              <div className="pb-3">
                {fx.lowPerf ? (
                  <p className="text-[11px] text-amber-400/90">Background blur is paused in Low Performance mode to keep your stream smooth.</p>
                ) : !canRealBlur() ? (
                  <p className="text-[11px] text-amber-400/90">This device/browser doesn't support real background blur.</p>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-xs mb-1"><span>Blur intensity</span><span className="text-white/50">{fx.blurRadius}</span></div>
                    <input type="range" min="3" max="20" value={fx.blurRadius} data-testid="slider-blur-radius"
                      onChange={(e) => setFx((f) => ({ ...f, blurRadius: Number(e.target.value) }))} className="w-full accent-taven-gold" />
                    <p className="text-[11px] text-white/50 mt-1.5">💡 Hold products close to the camera to keep them perfectly sharp.</p>
                  </>
                )}
              </div>
            )}

            {/* Product Accurate Mode */}
            <div className="flex items-center justify-between py-3 border-t border-white/10">
              <div className="flex items-center gap-2"><Palette className="w-4 h-4 text-taven-orange" /><div><p className="text-sm font-bold">Product Accurate Mode</p><p className="text-[10px] text-white/50">Keeps product colors & detail true</p></div></div>
              <button onClick={() => setFx((f) => ({ ...f, productAccurate: !f.productAccurate }))} data-testid="toggle-product-accurate"
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${fx.productAccurate ? "bg-taven-orange" : "bg-white/20"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${fx.productAccurate ? "translate-x-5" : ""}`} />
              </button>
            </div>

            {/* Low Performance Mode */}
            <div className="flex items-center justify-between py-3 border-t border-white/10">
              <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-white/70" /><div><p className="text-sm font-bold">Low Performance Mode</p><p className="text-[10px] text-white/50">Disables heavy blur on weaker devices</p></div></div>
              <button onClick={() => setFx((f) => ({ ...f, lowPerf: !f.lowPerf }))} data-testid="toggle-low-perf"
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${fx.lowPerf ? "bg-taven-gold" : "bg-white/20"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${fx.lowPerf ? "translate-x-5" : ""}`} />
              </button>
            </div>

            <div className="flex gap-2 mt-4">
              <button onClick={saveLook} data-testid="save-default-look-btn" className="flex-1 flex items-center justify-center gap-1.5 bg-taven-gold text-black text-xs font-bold uppercase tracking-wider py-3 hover:bg-white"><Save className="w-4 h-4" /> Save as my default look</button>
              <button onClick={resetLook} data-testid="reset-look-btn" title="Reset default look" className="flex items-center justify-center gap-1.5 border border-white/20 text-white/70 text-xs font-bold uppercase tracking-wider px-3 py-3 hover:border-white/50"><RotateCcw className="w-4 h-4" /></button>
            </div>
            <button onClick={() => setShowFilters(false)} className="w-full mt-2 bg-taven-orange text-white text-xs font-bold uppercase tracking-wider py-3 hover:bg-[#d9561a]" data-testid="filters-done-btn">Done</button>
          </div>
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center px-4" onClick={() => setShowInvite(false)}>
          <div className="bg-white text-black max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="cohost-invite-modal">
            <div className="flex items-center justify-between mb-3"><h3 className="font-black font-display">Invite a Co-host</h3><button onClick={() => setShowInvite(false)}><X className="w-5 h-5" /></button></div>
            <p className="text-sm text-muted-foreground mb-3">Enter the email of an approved TAVEN host to bring them on stage with you.</p>
            <form onSubmit={invite} className="flex gap-2">
              <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="email" placeholder="cohost@email.com" data-testid="cohost-email-input" className="flex-1 border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-taven-gold" />
              <button data-testid="cohost-invite-send" className="bg-taven-orange text-white px-4 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#d9561a]">Invite</button>
            </form>
          </div>
        </div>
      )}

      {showPicker && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center px-4" onClick={() => setShowPicker(false)}>
          <div className="bg-white text-black max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="product-picker">
            <div className="flex items-center justify-between mb-1"><h3 className="font-black font-display">Live Products</h3><button onClick={() => setShowPicker(false)}><X className="w-5 h-5" /></button></div>
            <p className="text-xs text-muted-foreground mb-4">Only approved TAVEN catalog products can be sold live. External links or non-TAVEN products are not allowed.</p>

            {showcase.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-bold uppercase tracking-wider text-taven-gold mb-2">In this live ({showcase.length})</p>
                <div className="space-y-2">
                  {showcase.map((p) => (
                    <div key={p.handle} className="flex items-center gap-3 border border-black/10 p-2" data-testid={`showcase-item-${p.handle}`}>
                      {p.image && <img src={p.image} alt="" className="w-10 h-10 object-cover" />}
                      <div className="flex-1 min-w-0"><p className="text-xs font-semibold line-clamp-1">{p.title}</p><p className="text-xs text-taven-orange font-bold">{money(p.price)}</p></div>
                      <button onClick={() => feature(p.handle)} data-testid={`feature-${p.handle}`} className={`text-[10px] font-bold uppercase px-2 py-1.5 flex items-center gap-1 ${pinned?.handle === p.handle ? "bg-taven-gold text-black" : "bg-black/5 hover:bg-taven-gold hover:text-black"}`}><Star className="w-3 h-3" /> {pinned?.handle === p.handle ? "Featured" : "Feature"}</button>
                      <button onClick={() => removeFromLive(p.handle)} data-testid={`remove-${p.handle}`} className="text-black/40 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs font-bold uppercase tracking-wider text-black/50 mb-2">Add from TAVEN catalog</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {products.map((p) => {
                const inLive = showcase.some((s) => s.handle === p.handle);
                return (
                  <button key={p.handle} onClick={() => addToLive(p.handle)} disabled={inLive} data-testid={`add-product-${p.handle}`} className={`text-left border p-2 relative ${inLive ? "border-taven-gold opacity-60 cursor-default" : "border-black/10 hover:border-taven-gold"}`}>
                    {p.image && <img src={p.image} alt="" className="w-full aspect-square object-cover mb-2" />}
                    <p className="text-xs font-semibold line-clamp-2">{p.title}</p>
                    <p className="text-xs text-taven-orange font-bold">{money(p.price)}</p>
                    <span className={`absolute top-1 right-1 text-[9px] font-bold uppercase px-1.5 py-0.5 flex items-center gap-0.5 ${inLive ? "bg-taven-gold text-black" : "bg-black text-white"}`}>{inLive ? "Added" : <><Plus className="w-2.5 h-2.5" /> Add</>}</span>
                  </button>
                );
              })}
              {products.length === 0 && <p className="col-span-full text-sm text-muted-foreground py-6 text-center">Loading products…</p>}
            </div>
          </div>
        </div>
      )}

      {/* Viewer shoppable product drawer */}
      {showShop && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center" onClick={() => setShowShop(false)}>
          <div className="bg-white text-black w-full sm:max-w-md max-h-[80vh] overflow-y-auto p-5 rounded-t-2xl sm:rounded-none" onClick={(e) => e.stopPropagation()} data-testid="live-shop-drawer">
            <div className="flex items-center justify-between mb-4"><h3 className="font-black font-display flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-taven-orange" /> Shop this Live</h3><button onClick={() => setShowShop(false)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              {showcase.map((p) => (
                <div key={p.handle} className="flex items-center gap-3 border border-black/10 p-2" data-testid={`shop-item-${p.handle}`}>
                  {p.image && <img src={p.image} alt="" className="w-16 h-16 object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold line-clamp-2">{p.title}</p>
                    <p className="text-sm text-taven-orange font-black">{money(p.price)}{!p.available && <span className="ml-2 text-[10px] uppercase text-red-500">Sold out</span>}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => buyProductNow(p)} data-testid={`shop-buy-${p.handle}`} className="bg-taven-orange text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-[#d9561a]">Buy Now</button>
                    <button onClick={() => addProductToCart(p)} data-testid={`shop-add-${p.handle}`} className="bg-taven-black text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-taven-gold hover:text-black flex items-center gap-1 justify-center"><ShoppingCart className="w-3 h-3" /> Add</button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => { setShowShop(false); setShowCart(true); }} className="w-full mt-4 border border-taven-black text-taven-black text-xs font-bold uppercase tracking-wider py-2.5 hover:bg-taven-black hover:text-white flex items-center justify-center gap-2"><ShoppingCart className="w-4 h-4" /> View Cart ({count})</button>
          </div>
        </div>
      )}

      {/* In-live cart panel */}
      {showCart && (
        <div className="fixed inset-0 z-[85] bg-black/70 flex items-end sm:items-center sm:justify-end" onClick={() => setShowCart(false)}>
          <div className="bg-white text-black w-full sm:max-w-sm sm:h-full max-h-[85vh] overflow-y-auto p-5 flex flex-col rounded-t-2xl sm:rounded-none" onClick={(e) => e.stopPropagation()} data-testid="live-cart-panel">
            <div className="flex items-center justify-between mb-4"><h3 className="font-black font-display flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-taven-orange" /> Live Cart</h3><button onClick={() => setShowCart(false)}><X className="w-5 h-5" /></button></div>
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground py-10 text-center flex-1">Your cart is empty. Tap <strong>Shop</strong> to add products from this live.</p>
            ) : (
              <>
                <div className="space-y-3 flex-1">
                  {items.map((i) => (
                    <div key={i.variantId} className="flex items-center gap-3 border-b border-black/5 pb-3" data-testid={`cart-item-${i.handle}`}>
                      {i.image && <img src={i.image} alt="" className="w-14 h-14 object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold line-clamp-2">{i.title}</p>
                        <p className="text-xs text-taven-orange font-black mt-0.5">{money(i.price)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <button onClick={() => updateQty(i.variantId, i.quantity - 1)} className="w-6 h-6 border border-black/15 flex items-center justify-center hover:border-taven-gold"><Minus className="w-3 h-3" /></button>
                          <span className="text-xs font-bold w-5 text-center">{i.quantity}</span>
                          <button onClick={() => updateQty(i.variantId, i.quantity + 1)} className="w-6 h-6 border border-black/15 flex items-center justify-center hover:border-taven-gold"><Plus className="w-3 h-3" /></button>
                          <button onClick={() => removeItem(i.variantId)} className="ml-auto text-black/40 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-4 mt-2 border-t border-black/10">
                  <div className="flex items-center justify-between mb-3"><span className="text-sm font-bold">Subtotal</span><span className="text-lg font-black font-display">{money(subtotal)}</span></div>
                  <button onClick={cartCheckout} data-testid="live-checkout-btn" className="w-full bg-taven-orange text-white font-bold uppercase tracking-wider py-3.5 hover:bg-[#d9561a]">Secure Checkout</button>
                  <p className="text-[11px] text-muted-foreground text-center mt-2">Checkout is protected on shop.taven.store.</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
